import styles from "./MeshField.module.css";

/**
 * The picture mark, grown until it is a wall.
 *
 * K60's mark is grating mesh seen from an angle: slim parallelograms, sheared
 * 22° off vertical, in grey, red and dusty rose. Everywhere else in this app
 * it stays small, because a pattern behind millimetres makes them harder to
 * read (CI §3). The sign-in screen and the two steel areas of the start page
 * have no millimetres — there the product can stand as its own sign.
 *
 * It is a brand surface, and it holds still (idea 26, 18.10.2026; CI §6 „keine
 * Animation ohne Zweck"). It used to breathe for ever and lean away from the
 * pointer, on the start page too — the screen people see most, where motion
 * nobody asked for only pulls the eye off the work.
 *
 * `settle` is for the door alone: the cells are laid bar by bar on first
 * paint and a hard-edged band of light crosses the steel once (a sheen — with
 * an edge, not a blur; CI §6 forbids soft gradients, not light). Then it is
 * still. Under prefers-reduced-motion base.css ends both at once.
 */

const SHEAR = "skewX(-22)";
const COLUMNS = [30, 200, 370, 540];
const ROWS = [-80, 90, 260, 430, 600, 770, 940];
const CELL_W = 96;
const CELL_H = 140;

/* Depth: the grey columns further right sit further back and fade. Red and
   rose do not fade — dimmed red goes brown, and the accent must stay the
   brand's. */
const DEPTH = [1, 0.78, 0.58, 0.42];

type Tone = "grey" | "red" | "rose";

/** Which cell carries which tone. Fixed, so the field looks the same twice. */
function toneAt(col: number, row: number): Tone {
  const k = (col * 3 + row * 5) % 11;
  if (k === 0 || k === 7) return "red";
  if (k === 4 || k === 9) return "rose";
  return "grey";
}

const CELLS = ROWS.flatMap((y, row) =>
  COLUMNS.map((x, col) => ({
    x,
    y: y + (col % 2) * 70,
    col,
    row,
    tone: toneAt(col, row),
    depth: toneAt(col, row) === "grey" ? DEPTH[col]! : 1,
  })),
);

interface MeshFieldProps {
  className?: string;
  /** Lay the grating once on first paint and let the light cross it — the
   * sign-in screen's entrance. Without it the field stands as it is. */
  settle?: boolean;
}

export function MeshField({ className, settle = false }: MeshFieldProps) {
  return (
    <svg
      className={`${styles.field} ${className ?? ""}`}
      viewBox="0 0 600 960"
      preserveAspectRatio="xMidYMid slice"
      role="presentation"
      aria-hidden="true"
      focusable="false"
      data-settle={settle ? "" : undefined}
    >
      {/* The wordmark as a watermark, cut by the edge: one tone up from the
          steel, so it is felt before it is read. */}
      <text className={styles.watermark} x="-24" y="948">
        K60
      </text>

      <g transform={SHEAR} style={{ transformOrigin: "center" }}>
        {CELLS.map((cell, i) => (
          <rect
            key={`${cell.col}-${cell.row}`}
            className={`${styles.cell} ${styles[cell.tone]}`}
            style={{ "--i": i, "--depth": cell.depth } as React.CSSProperties}
            x={cell.x}
            y={cell.y}
            width={CELL_W}
            height={CELL_H}
            rx="2"
          />
        ))}
      </g>

      {/* The sheen: a hard-edged band of light, sheared like the bars, crossing
          once after the grating is laid. Only where it settles in — on a
          still field it would wait off the edge for nothing. */}
      {settle ? <rect className={styles.sheen} x="0" y="-200" width="70" height="1400" /> : null}
    </svg>
  );
}
